import { Component, Inject } from "@angular/core";
import { UntypedFormGroup, UntypedFormControl } from "@angular/forms";
import { HttpClient } from "@angular/common/http";
import {
  MatDialogRef,
  MAT_DIALOG_DATA,
  MatDialog,
} from "@angular/material/dialog";
import { Observable, Subscription } from "rxjs";
import { environment } from "../../../../environments/environment";
import { AuthenticationService } from "../../authentication/authentication/authentication.service";
import { ErrorHandlerDialogComponent } from "../../error-handler-dialog/error-handler-dialog.component";
import { ReferenceService } from "src/app/services/reference.service";
import { ArchitectureDTO } from "src/app/model/ArchitectureDTO";
import { APLStatusDTO } from "src/app/model/APLStatusDTO";
import { ArchitectureTypeDTO } from "src/app/model/ArchitectureTypeDTO";
import { BodystyleDTO } from "src/app/model/BodystyleDTO";
import { RegionDTO } from "src/app/model/RegionDTO";
import { BrandDTO } from "src/app/model/BrandDTO";
import { DriveConfigDTO } from "src/app/model/DriveConfigDTO";
import { EngineeringClassDTO } from "src/app/model/EngineeringClassDTO";
import { LaunchProbabilityDTO } from "src/app/model/LaunchProbability";
import { GlobalSegmentDTO } from "src/app/model/GlobalSegmentDTO";
import { OEMDTO } from "src/app/model/OEMDTO";
import { PropulsionTypeDTO } from "src/app/model/PropulsionTypeDTO";
import { BodystyleValueDTO } from "src/app/model/BodystyleValueDTO";

interface TableInfo {
  columns: { [key: string]: string };
  keys: string[];
}

@Component({
    selector: "app-reference-table-dialog",
    templateUrl: "./reference-table-dialog.component.html",
    styleUrls: ["./reference-table-dialog.component.scss"],
    standalone: false
})
export class ReferenceTableDialogComponent {
  group: UntypedFormGroup;
  isValidEntry = true;
  name = "name";

  constructor(
    public readonly dialog: MatDialog,
    public readonly dialogRef: MatDialogRef<ReferenceTableDialogComponent>,
    private readonly auth: AuthenticationService,
    private readonly http: HttpClient,
    private readonly referenceService: ReferenceService,
    @Inject(MAT_DIALOG_DATA)
    private data: {
      table: string;
      tableInfo: TableInfo;
      value: { [key: string]: any };
      dataSource: { [key: string]: any };
    }
  ) {
    this.group = new UntypedFormGroup(
      Object.fromEntries(
        this.getEntries().map(([key, val]) => [
          key,
          new UntypedFormControl(val),
        ])
      )
    );
  }

  public getData(): string {
    return JSON.stringify(this.data, null, "    ");
  }

  public getValue(): any {
    return Object.fromEntries(this.getEntries());
  }

  public getEntries(): [string, any][] {
    const columns = this.data.tableInfo.columns;
    const val = this.data.value || null;
    return Object.entries(columns).map(([col]) => [col, val && val[col]]);
  }

  public getEditedValue(): any {
    return Object.fromEntries(
      Object.entries(this.group.controls).map(([key, control]) => [
        key,
        control.value.trim(),
      ])
    );
  }

  public close(json): void {
    this.dialogRef.close(json);
  }

  private create(out: any): Observable<any> {
    switch (this.data.table) {
      case "status":
        out.enabled = true;
        return this.referenceService.create<APLStatusDTO>(
          environment.serviceURL + "/crud/createAPLStatus",
          out
        );
      case "architecture":
        out.enabled = true;
        return this.referenceService.create<ArchitectureDTO>(
          environment.serviceURL + "/crud/createArchitecture",
          out
        );
      case "architecturetype":
        out.enabled = true;
        return this.referenceService.create<ArchitectureTypeDTO>(
          environment.serviceURL + "/crud/createArchitectureType",
          out
        );
      case "bodystylevalue":
        out.enabled = true;
        return this.referenceService.create<BodystyleValueDTO>(
          environment.serviceURL + "/crud/createBodystyleValue",
          out
        );
      case "brand":
        out.enabled = true;
        return this.referenceService.create<BrandDTO>(
          environment.serviceURL + "/crud/createBrand",
          out
        );
      case "driveconfig":
        out.enabled = true;
        return this.referenceService.create<DriveConfigDTO>(
          environment.serviceURL + "/crud/createDriveConfig",
          out
        );
      case "engineeringclass":
        out.enabled = true;
        return this.referenceService.create<EngineeringClassDTO>(
          environment.serviceURL + "/crud/createEngineeringClass",
          out
        );
      case "globalsegment":
        out.enabled = true;
        return this.referenceService.create<GlobalSegmentDTO>(
          environment.serviceURL + "/crud/createGlobalSegment",
          out
        );
      case "oem":
        out.enabled = true;
        return this.referenceService.create<OEMDTO>(
          environment.serviceURL + "/crud/createOEM",
          out
        );
      case "propulsiontype":
        out.enabled = true;
        return this.referenceService.create<PropulsionTypeDTO>(
          environment.serviceURL + "/crud/createPropulsionType",
          out
        );
      case "region":
        out.enabled = true;
        return this.referenceService.create<RegionDTO>(
          environment.serviceURL + "/crud/createRegion",
          out
        );
      case "launchprobability":
        out.enabled = true;
        return this.referenceService.create<LaunchProbabilityDTO>(
          environment.serviceURL + "/crud/createLaunchProbability",
          out
        );
    }
  }

  private update(out: any, oldId: string): Observable<any> {
    switch (this.data.table) {
      case "status":
        const newAPLStatus: APLStatusDTO = {
          name: out.name,
          enabled: this.data.value.enabled,
        };
        return this.referenceService.update<APLStatusDTO>(
          environment.serviceURL + "/crud/updateAPLStatus",
          oldId,
          newAPLStatus
        );
      case "architecture":
        out.enabled = this.data.value.enabled;
        return this.referenceService.update<ArchitectureDTO>(
          environment.serviceURL + "/crud/updateArchitecture",
          oldId,
          out
        );
      case "architecturetype":
        out.enabled = this.data.value.enabled;
        return this.referenceService.update<ArchitectureTypeDTO>(
          environment.serviceURL + "/crud/updateArchitectureType",
          oldId,
          out
        );
      case "bodystylevalue":
        out.enabled = this.data.value.enabled;
        return this.referenceService.update<BodystyleValueDTO>(
          environment.serviceURL + "/crud/updateBodystyleValue",
          oldId,
          out
        );
      case "brand":
        out.enabled = this.data.value.enabled;
        return this.referenceService.update<BrandDTO>(
          environment.serviceURL + "/crud/updateBrand",
          oldId,
          out
        );
      case "driveconfig":
        out.enabled = this.data.value.enabled;
        return this.referenceService.update<DriveConfigDTO>(
          environment.serviceURL + "/crud/updateDriveConfig",
          oldId,
          out
        );
      case "engineeringclass":
        out.enabled = this.data.value.enabled;
        return this.referenceService.update<EngineeringClassDTO>(
          environment.serviceURL + "/crud/updateEngineeringClass",
          oldId,
          out
        );
      case "globalsegment":
        out.enabled = this.data.value.enabled;
        return this.referenceService.update<GlobalSegmentDTO>(
          environment.serviceURL + "/crud/updateGlobalSegment",
          oldId,
          out
        );
      case "oem":
        out.enabled = this.data.value.enabled;
        return this.referenceService.update<OEMDTO>(
          environment.serviceURL + "/crud/updateOEM",
          oldId,
          out
        );
      case "propulsiontype":
        out.enabled = this.data.value.enabled;
        return this.referenceService.update<PropulsionTypeDTO>(
          environment.serviceURL + "/crud/updatePropulsionType",
          oldId,
          out
        );
      case "region":
        out.enabled = this.data.value.enabled;
        return this.referenceService.update<RegionDTO>(
          environment.serviceURL + "/crud/updateRegion",
          oldId,
          out
        );
      case "launchprobability":
        out.enabled = this.data.value.enabled;
        return this.referenceService.update<LaunchProbabilityDTO>(
          environment.serviceURL + "/crud/updateLaunchProbability",
          oldId,
          out
        );
    }
  }

  public async save(): Promise<any> {
    const out = this.getEditedValue();
    const method: string = !!this.data.value ? "PUT" : "POST";
    const oldId = this.data.value?.name;

    // const url: URL = this.getValueUrl(method);
    this.validateUserChange(out);
    if (this.isValidEntry) {
      try {
        let json;
        //POST is create
        if (method === "POST") {
          json = this.create(out).subscribe({
            next: () => {
              this.close(true);
              window.location.reload();
            },
            error: () => {
              this.dialog.open(ErrorHandlerDialogComponent, {
                data: {
                  message: `The reference value ${out.name} could not be created because it already exists.`,
                },
              });
            },
          });
        }
        //PUT is update
        else {
          json = this.update(out, oldId).subscribe({
            next: () => {
              this.close(true);
              window.location.reload();
            },
            error: () => {
              this.dialog.open(ErrorHandlerDialogComponent, {
                data: {
                  message: `The reference value ${out.name} could not be updated because it already exists or it is being used.`,
                },
              });
            },
          });
        }
      } catch (e) {
        alert(e.message);
      }
    }
  }

  private deleteHelper(): Observable<any> {
    switch (this.data.table) {
      case "status":
        const aplStatusToDelete: APLStatusDTO = {
          name: this.data.value.name,
          enabled: this.data.value.enabled,
        };
        return this.referenceService.delete<APLStatusDTO>(
          environment.serviceURL + "/crud/deleteAPLStatus",
          aplStatusToDelete
        );
      case "architecture":
        const architectureToDelete: ArchitectureDTO = {
          name: this.data.value.name,
          enabled: this.data.value.enabled,
        };
        return this.referenceService.delete<ArchitectureDTO>(
          environment.serviceURL + "/crud/deleteArchitecture",
          architectureToDelete
        );
      case "architecturetype":
        const architectureTypeToDelete: ArchitectureTypeDTO = {
          name: this.data.value.name,
          enabled: this.data.value.enabled,
        };
        return this.referenceService.delete<ArchitectureTypeDTO>(
          environment.serviceURL + "/crud/deleteArchitectureType",
          architectureTypeToDelete
        );
      case "bodystylevalue":
        const bodystyleValueToDelete: BodystyleValueDTO = {
          name: this.data.value.name,
          enabled: this.data.value.enabled,
        };
        return this.referenceService.delete<BodystyleValueDTO>(
          environment.serviceURL + "/crud/deleteBodystyleValue",
          bodystyleValueToDelete
        );
      case "brand":
        const brandToDelete: BrandDTO = {
          name: this.data.value.name,
          enabled: this.data.value.enabled,
        };
        return this.referenceService.delete<BrandDTO>(
          environment.serviceURL + "/crud/deleteBrand",
          brandToDelete
        );
      case "driveconfig":
        const driveConfigToDelete: DriveConfigDTO = {
          name: this.data.value.name,
          enabled: this.data.value.enabled,
        };
        return this.referenceService.delete<DriveConfigDTO>(
          environment.serviceURL + "/crud/deleteDriveConfig",
          driveConfigToDelete
        );
      case "engineeringclass":
        const engineeringClassToDelete: EngineeringClassDTO = {
          name: this.data.value.name,
          enabled: this.data.value.enabled,
        };
        return this.referenceService.delete<EngineeringClassDTO>(
          environment.serviceURL + "/crud/deleteEngineeringClass",
          engineeringClassToDelete
        );
      case "globalsegment":
        const globalSegmentToDelete: GlobalSegmentDTO = {
          name: this.data.value.name,
          enabled: this.data.value.enabled,
        };
        return this.referenceService.delete<GlobalSegmentDTO>(
          environment.serviceURL + "/crud/deleteGlobalSegment",
          globalSegmentToDelete
        );
      case "oem":
        const oemToDelete: OEMDTO = {
          name: this.data.value.name,
          enabled: this.data.value.enabled,
        };
        return this.referenceService.delete<OEMDTO>(
          environment.serviceURL + "/crud/deleteOEM",
          oemToDelete
        );
      case "propulsiontype":
        const propulsionTypeToDelete: PropulsionTypeDTO = {
          name: this.data.value.name,
          enabled: this.data.value.enabled,
        };
        return this.referenceService.delete<PropulsionTypeDTO>(
          environment.serviceURL + "/crud/deletePropulsionType",
          propulsionTypeToDelete
        );
      case "region":
        const regionToDelete: RegionDTO = {
          name: this.data.value.name,
          acronym: this.data.value.acronym,
          enabled: this.data.value.enabled,
        };
        return this.referenceService.delete<RegionDTO>(
          environment.serviceURL + "/crud/deleteRegion",
          regionToDelete
        );
      case "launchprobability":
        const launchProbabilityToDelete: LaunchProbabilityDTO = {
          name: this.data.value.name,
          enabled: this.data.value.enabled,
        };
        return this.referenceService.delete<LaunchProbabilityDTO>(
          environment.serviceURL + "/crud/deleteLaunchProbability",
          launchProbabilityToDelete
        );
    }
  }

  public async delete(): Promise<Subscription> {
    let warning =
      "WARNING: Deleting this reference value will have the following effect(s): \n";
    const table = this.data.table;
    const value = this.data.value.name;

    if (
      table === "driveconfig" ||
      table === "oem" ||
      table === "architecturetype" ||
      table === "engineeringclass" ||
      table === "launchprobability" ||
      table === "globalsegment"
    ) {
      warning += `\n- Any previous vehicle version that has referenced ${value} will be deleted, possibly resulting in inaccurate/incomplete vehicle version histories`;
    }

    if (
      table === "propulsiontype" ||
      table === "status" ||
      table === "brand" ||
      table === "driveconfig" ||
      table === "region"
    ) {
      warning += `\n- Any propulsion record that currently references ${value} will be deleted`;
    }

    if (table === "bodystylevalue" || table === "region") {
      warning += `\n- Any bodystyle lifecycle record that currently references ${value} will be deleted`;
    }

    if (table === "engineeringclass") {
      warning += `\n- Any bodystyle cycle that currently references ${value} will be deleted, possibly resulting in incomplete bodystyle lifecycle records`;
    }

    warning += "\n\nOnce a record is deleted, it cannot be recovered. Proceed?";

    const confirmed = confirm(warning);

    if (!confirmed) {
      return null;
    }

    try {
      return this.deleteHelper().subscribe({
        next: () => {
          this.close(true);
          window.location.reload();
        },
        error: () => {
          this.dialog.open(ErrorHandlerDialogComponent, {
            data: {
              message:
                `The reference value ${this.data.value.name} could not be deleted because it is being referenced in the latest version of ` +
                `one or more vehicle records. Please make sure that no vehicle record is currently referencing ${this.data.value.name} before trying again.`,
            },
          });
        },
      });
    } catch (e) {
      alert(e);
    }
  }

  public validateUserChange(userInput: any): void {
    this.isValidEntry = true;
    Object.keys(this.data.dataSource).forEach((key: string) => {
      if (
        userInput[this.name].toLowerCase() ===
        this.data.dataSource[key][this.name].toLowerCase()
      ) {
        if (
          !this.data.value ||
          userInput[this.name].toLowerCase() !==
            this.data.value[this.name].toLowerCase()
        ) {
          this.isValidEntry = false;
          this.dialog.open(ErrorHandlerDialogComponent, {
            data: {
              message: `${
                this.data.dataSource[key][this.name]
              } already exists.`,
              status: "Error: Duplicate Value",
            },
          });
        }
      }
    });
  }
}
